import { useEffect, useMemo, useState } from 'react'
import { Link, useSearchParams } from 'react-router'
import { useTranslation } from 'react-i18next'
import type { TFunction } from 'i18next'

import { FormSelect } from '../../components/FormField'
import { RecordPicker } from '../../components/RecordPicker'
import { describeBookRecord, type BookRecord } from '../../components/bookRecord'
import { apiErrorMessage } from '../../lib/errors'
import { formatDate } from '../../lib/format'
import { keepingView } from '../../lib/tableAddress'
import type { TableColumn } from '../../lib/tableView'
import { useAddressTableView } from '../../lib/useAddressTableView'
import { filterChoice, useFilterRecord } from '../../lib/useFilterRecord'
import { FilterCell, FilterSummary, SortableHeader } from '../../components/TableHeads'
import { TableScroll } from '../../components/TableScroll'
import { useAuth } from '../auth/context'
import { getCarrier, searchCarriers } from '../carriers/api'
import { alertFilterFrom, alertSearchFrom, isAlertListNarrowed } from './alertFilter'
import {
  ALERT_PAPERS,
  ALERT_SITUATIONS,
  OWNER_TYPES,
  listAlerts,
  runEvaluation,
  type AlertFilter,
  type AlertSituation,
  type ComplianceAlert,
  type EvaluationSummary,
  type OwnerType,
} from './api'

/** A carrier's name, asked of the carrier itself: an address holds only its id. */
const carrierName = (id: string) => getCarrier(id).then((carrier) => carrier.name)

/**
 * What the list's columns sort and filter by (TRKC-110). Whose papers, which paper and
 * whether it lapsed are filtered from the bar above — the server's filters, before the list's
 * ceiling — so those headings sort and their boxes stay empty; the owner's name and the
 * expiry are filtered here.
 */
function alertColumns(t: TFunction): TableColumn<ComplianceAlert>[] {
  return [
    { key: 'document', sortValue: (alert) => t(`documents.types.${alert.documentType}`) },
    {
      key: 'owner',
      sortValue: (alert) => alert.ownerName,
      filter: { kind: 'text', text: (alert) => alert.ownerName },
    },
    {
      key: 'expiresOn',
      sortValue: (alert) => alert.expiresOn,
      filter: { kind: 'onOrAfter', value: (alert) => alert.expiresOn },
    },
    { key: 'standing', sortValue: (alert) => alert.daysRemaining },
  ]
}

/** The heading each column's filter box is named after. */
const ALERT_HEADINGS: Record<string, string> = {
  document: 'alerts.document',
  owner: 'alerts.ownerColumn',
  expiresOn: 'alerts.expiresOn',
  standing: 'alerts.standing',
}

/** No list yet: one array, so the view is not recomputed for an empty one every render. */
const NO_ALERTS: ComplianceAlert[] = []

/**
 * Everything the platform noticed while nobody was looking.
 *
 * One line per document, never per alert: a certificate that crossed 30, 15 and 7 days
 * has three rows in the database — the escalation is real and is what they are for —
 * but telling somebody three times about one piece of paper is how a list gets ignored.
 * The server collapses them to the narrowest window each paper reached.
 *
 * Narrowed by whose papers, which paper, whether it lapsed and one carrier with its fleet
 * (TRKC-97) — server-side, before the list's ceiling, and kept in the address like the
 * board's filters (TRKC-90), so going back to the list from a record finds it as it was.
 */
export function AlertsPage() {
  const { t, i18n } = useTranslation()
  const { user } = useAuth()

  // The controls answer from state the page owns, with the address as its copy — the
  // board's reason: the router applies an address change inside a transition.
  const [searchParams, setSearchParams] = useSearchParams()
  const address = searchParams.toString()
  const [filter, setFilter] = useState(() => alertFilterFrom(searchParams))
  // The carrier chosen in the filter's picker, with its name — the address holds only the id.
  const [chosenCarrier, setChosenCarrier] = useState<BookRecord | null>(null)

  const [alerts, setAlerts] = useState<ComplianceAlert[] | null>(null)
  // The fallback sentence travels WITH the failure, because this screen has two of
  // them and they mean opposite things: the list would not load, or the check would
  // not run while the list sits there perfectly loaded.
  const [failure, setFailure] = useState<{ error: unknown; fallbackKey: string } | null>(null)
  const [isRunning, setIsRunning] = useState(false)
  const [lastRun, setLastRun] = useState<EvaluationSummary | null>(null)
  // Bumped after a run: the list is read again under the filter it shows.
  const [reading, setReading] = useState(0)

  // The address can change under the page — the menu's plain link, the browser's Back —
  // and the list follows it; following its own writes changes nothing.
  useEffect(() => {
    setFilter(alertFilterFrom(new URLSearchParams(address)))
  }, [address])

  const narrow = (changes: Partial<AlertFilter>) => {
    const next = { ...filter, ...changes }
    setFilter(next)
    // The column view the address holds goes along (TRKC-110).
    setSearchParams((current) => keepingView(alertSearchFrom(next), current), { replace: true })
  }

  const asked = alertSearchFrom(filter).toString()
  const columns = useMemo(() => alertColumns(t), [t])
  const view = useAddressTableView(alerts ?? NO_ALERTS, columns)

  // A carrier id in the address can name nobody, or a carrier past any list's cap: unless
  // it is the one just chosen, it is asked of the carrier itself — the board's rule (#36).
  const carrierRecord = useFilterRecord(filter.carrierId, chosenCarrier, carrierName)
  const holdForCarrier = carrierRecord.state === 'checking' || carrierRecord.state === 'gone'

  useEffect(() => {
    if (carrierRecord.state === 'gone') {
      const next = { ...filter, carrierId: undefined }
      setFilter(next)
      setSearchParams((current) => keepingView(alertSearchFrom(next), current), { replace: true })
    }
  }, [carrierRecord.state, filter, setSearchParams])

  // Every change of filter asks the server again, and answers can land out of order: only
  // the latest request may write, so a slower answer to a filter the screen has left — or
  // its failure — never replaces the list the controls describe (GPT-5.6 Sol on #32).
  useEffect(() => {
    if (holdForCarrier) {
      return
    }

    let active = true

    listAlerts(alertFilterFrom(new URLSearchParams(asked)))
      .then((found) => {
        if (active) {
          setAlerts(found)
          setFailure((current) => (current?.fallbackKey === 'alerts.loadError' ? null : current))
        }
      })
      .catch((caught: unknown) => {
        if (active) {
          setAlerts(null)
          setFailure({ error: caught, fallbackKey: 'alerts.loadError' })
        }
      })

    return () => {
      active = false
    }
  }, [asked, holdForCarrier, reading])

  async function handleRun() {
    setIsRunning(true)
    setFailure(null)

    try {
      // The list is re-read rather than patched from the summary: the summary counts
      // what changed, and the screen has to show what IS.
      setLastRun(await runEvaluation())
      setReading((count) => count + 1)
    } catch (caught) {
      setFailure({ error: caught, fallbackKey: 'alerts.runError' })
    } finally {
      setIsRunning(false)
    }
  }

  // Kept apart from an empty list: a list that failed to load is not a list still
  // loading, and conflating them leaves "Loading…" spinning under the error banner.
  const isLoading = alerts === null && failure === null

  return (
    <section className="page">
      <header className="page__header page__header--row">
        <div>
          <h1>{t('alerts.title')}</h1>
          <p className="page__subtitle">{t('alerts.subtitle')}</p>
        </div>
        <div className="page__actions">
          {/* Only where it can be acted on: a dispatcher who cannot change the
              windows does not need a link to a screen that refuses him. */}
          {(user?.roles.includes('Admin') ?? false) && (
            <Link className="button button--quiet" to="/alerts/windows">
              {t('alerts.settings')}
            </Link>
          )}
          <button
            type="button"
            className="button button--quiet"
            onClick={() => void handleRun()}
            disabled={isRunning}
          >
            {isRunning ? t('alerts.checking') : t('alerts.checkNow')}
          </button>
        </div>
      </header>

      <div className="toolbar toolbar--filters">
        <FormSelect
          name="kindFilter"
          label={t('alerts.filters.kind')}
          value={filter.ownerType ?? ''}
          onChange={(value) => narrow({ ownerType: value === '' ? undefined : (value as OwnerType) })}
          options={[
            { value: '', label: t('alerts.filters.anyKind') },
            ...OWNER_TYPES.map((kind) => ({ value: kind, label: t(`alerts.filters.kinds.${kind}`) })),
          ]}
        />
        {/* By the name each paper has in the rows below, in the language's own order. */}
        <FormSelect
          name="paperFilter"
          label={t('alerts.filters.paper')}
          value={filter.documentType ?? ''}
          onChange={(value) => narrow({ documentType: value === '' ? undefined : value })}
          options={[
            { value: '', label: t('alerts.filters.anyPaper') },
            ...ALERT_PAPERS.map((paper) => ({ value: paper, label: t(`documents.types.${paper}`) })).sort((a, b) =>
              a.label.localeCompare(b.label, i18n.language),
            ),
          ]}
        />
        <FormSelect
          name="situationFilter"
          label={t('alerts.filters.situation')}
          value={filter.situation ?? ''}
          onChange={(value) => narrow({ situation: value === '' ? undefined : (value as AlertSituation) })}
          options={[
            { value: '', label: t('alerts.filters.anySituation') },
            ...ALERT_SITUATIONS.map((situation) => ({
              value: situation,
              label: t(`alerts.filters.situations.${situation}`),
            })),
          ]}
        />
        {/* The carrier kept in the address is shown as chosen, as on the board. */}
        <RecordPicker<BookRecord>
          name="carrierFilter"
          label={t('alerts.filters.carrier')}
          value={filterChoice(filter.carrierId, carrierRecord, t('alerts.filters.linkedCarrier'))}
          onChange={(record) => {
            setChosenCarrier(record)
            narrow({ carrierId: record?.id })
          }}
          search={searchCarriers}
          describe={(record) => describeBookRecord(record, t)}
          noneLabel={t('alerts.filters.anyCarrier')}
          placeholder={t('alerts.filters.anyCarrier')}
        />
      </div>

      {failure !== null && (
        <p className="alert" role="alert">
          {apiErrorMessage(failure.error, t, failure.fallbackKey)}
        </p>
      )}

      {lastRun !== null && (
        <p className="placeholder" role="status">
          {t('alerts.ranSummary', {
            checked: lastRun.ownersEvaluated,
            raised: lastRun.alertsRaised,
            resolved: lastRun.alertsResolved,
          })}
          {/* Never hidden: a run that quietly did nine tenths of its work looks
              exactly like one that finished. */}
          {lastRun.conflicts > 0 && ` ${t('alerts.ranConflicts', { count: lastRun.conflicts })}`}
        </p>
      )}

      {isLoading ? (
        <p className="placeholder">{t('alerts.loading')}</p>
      ) : alerts === null ? null : alerts.length === 0 ? (
        <p className="placeholder">
          {isAlertListNarrowed(filter) ? t('alerts.filters.noneMatch') : t('alerts.empty')}
        </p>
      ) : (
        <TableScroll label={t('alerts.title')}>
          <table className="table">
            <thead>
              <tr>
                {columns.map((column) => (
                  <SortableHeader key={column.key} view={view} column={column.key}>
                    {t(ALERT_HEADINGS[column.key] ?? column.key)}
                  </SortableHeader>
                ))}
              </tr>
              <tr className="table__filters">
                {columns.map((column) => (
                  <FilterCell key={column.key} view={view} column={column} label={t(ALERT_HEADINGS[column.key] ?? column.key)} />
                ))}
              </tr>
            </thead>
            <tbody>
              {view.rows.length === 0 && (
                <tr>
                  <td colSpan={columns.length} className="placeholder">
                    {t('alerts.filters.noneMatch')}
                  </td>
                </tr>
              )}
              {view.rows.map((alert) => (
                <tr key={alert.documentId}>
                  {/* The same label the document panel uses — one paper, one name,
                      wherever it is mentioned. */}
                  <td>{t(`documents.types.${alert.documentType}`)}</td>
                  <td>
                    <Link to={screenFor(alert)}>{alert.ownerName}</Link>{' '}
                    <span className="muted">{t(`alerts.ownerKind.${alert.ownerType}`)}</span>
                  </td>
                  <td>{formatDate(alert.expiresOn, i18n.language)}</td>
                  <td>
                    <Standing alert={alert} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </TableScroll>
      )}
      {!isLoading && alerts !== null && alerts.length > 0 && <FilterSummary view={view} />}
    </section>
  )
}

/**
 * Expired reads as a state, not as a negative number of days left: "-3 days" is a
 * puzzle, "expired 3 days ago" is a fact somebody can act on.
 */
function Standing({ alert }: { alert: ComplianceAlert }) {
  const { t } = useTranslation()

  if (alert.isExpired) {
    return (
      <span className="badge badge--blocked">
        {t('alerts.expiredAgo', { count: Math.abs(alert.daysRemaining) })}
      </span>
    )
  }

  // The expiry date is the last VALID day, so zero days left is not a countdown that
  // hit bottom — it is a paper that still works today and will not tomorrow. "0 days
  // left" would read as either.
  if (alert.daysRemaining === 0) {
    return <span className="badge badge--pending">{t('alerts.expiresToday')}</span>
  }

  return (
    <span className="badge badge--pending">
      {t('alerts.daysLeft', { count: alert.daysRemaining })}
    </span>
  )
}

/**
 * Where to go and do something about it. A fleet child has no screen of its own —
 * every one of them lives under its carrier — which is why the alert carries the
 * carrier id at all.
 */
function screenFor(alert: ComplianceAlert): string {
  switch (alert.ownerType) {
    case 'Broker':
      return `/brokers/${alert.ownerId}`
    case 'Carrier':
      return `/carriers/${alert.ownerId}`
    case 'Driver':
      return `/carriers/${alert.carrierId}/drivers/${alert.ownerId}`
    case 'Truck':
      return `/carriers/${alert.carrierId}/trucks/${alert.ownerId}`
    case 'Trailer':
      return `/carriers/${alert.carrierId}/trailers/${alert.ownerId}`
  }
}
